import { Component, ChangeDetectionStrategy, ChangeDetectorRef, inject, signal, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CheckboxModule } from 'primeng/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { CompanyService, CompanyDTO } from '../../services/company.service';
import { AuthService } from '../../services/auth.service';
import { CompanyMitConfigComponent } from './company-mit-config.component';
import { CompanyCustomerNotificationConfigComponent } from './company-customer-notification-config.component';
import type { CompanyConfig, DynamicField, DynamicFieldType, UpdateCompanyConfigRequest } from '../../core/models';

type DynamicFieldsScope = 'sale' | 'subscription';

@Component({
  selector: 'app-company-config',
  templateUrl: './company-config.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    ReactiveFormsModule,
    InputTextModule,
    FloatLabelModule,
    SelectModule,
    ButtonModule,
    CardModule,
    TabsModule,
    TooltipModule,
    ToggleSwitchModule,
    CheckboxModule,
    MatIconModule,
    CompanyMitConfigComponent,
    CompanyCustomerNotificationConfigComponent
  ]
})
export class CompanyConfigComponent implements OnInit {
  private fb = inject(FormBuilder);
  private companyService = inject(CompanyService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(CompanyMitConfigComponent) private mitConfigSection?: CompanyMitConfigComponent;
  @ViewChild(CompanyCustomerNotificationConfigComponent) private customerNotificationSection?: CompanyCustomerNotificationConfigComponent;

  configForm: FormGroup;
  loading = signal(false);
  initialLoad = signal(true);
  company = signal<CompanyDTO | null>(null);
  activeTab = signal<string>('facturacion');

  readonly dynamicFieldTypeOptions: { value: DynamicFieldType; label: string }[] = [
    { value: 'TEXT', label: 'Texto' },
    { value: 'NUMBER', label: 'Número' },
    { value: 'DATE', label: 'Fecha' },
    { value: 'BOOLEAN', label: 'Booleano' },
    { value: 'SELECT', label: 'Lista' }
  ];

  private auth = inject(AuthService);
  readonly showMitConfig = this.auth.hasRole('MIT_MANDATE_READ') || this.auth.hasRole('MIT_CONFIG_WRITE');

  tabs: { value: string; label: string; icon?: string; matIcon?: string }[] = [
    { value: 'facturacion', label: 'Facturación', icon: 'pi pi-file-check' },
    { value: 'ventas', label: 'Ventas', icon: 'pi pi-shopping-cart' },
    { value: 'suscripciones', label: 'Suscripciones', icon: 'pi pi-sync' },
    { value: 'notificaciones', label: 'Notificaciones', icon: 'pi pi-envelope' },
    ...(this.showMitConfig ? [{ value: 'mit', label: 'Mandatos', matIcon: 'handshake' }] : [])
  ];

  constructor() {
    this.configForm = this.fb.group({
      companyConfig: this.fb.group({
        companyInvoiceConfig: this.fb.group({
          enableInvoice: [false],
          enableAutoInvoice: [false],
          companySaleConfig: this.fb.group({
            dynamicFields: this.fb.array([])
          }),
          companySubscriptionConfig: this.fb.group({
            renewalsHourUTC: ['00:00', [Validators.required, Validators.pattern(/^\d{2}:\d{2}(:\d{2})?$/)]],
            dynamicFields: this.fb.array([])
          }),
          companyNotificationConfig: this.fb.group({
            domain: ['', [Validators.maxLength(255)]],
            sendEmail: ['', [Validators.email, Validators.maxLength(255)]],
            endEmailAlias: ['', [Validators.maxLength(200)]],
            replayEmail: ['', [Validators.email, Validators.maxLength(255)]],
            replayEmailAlias: ['', [Validators.maxLength(200)]]
          })
        })
      })
    });
  }

  ngOnInit(): void {
    this.loadCompany();
  }

  get saleDynamicFieldsArray(): FormArray {
    return this.getDynamicFieldsArray('sale');
  }

  get subscriptionDynamicFieldsArray(): FormArray {
    return this.getDynamicFieldsArray('subscription');
  }

  get notificationConfigGroup(): FormGroup {
    return this.configForm.get('companyConfig.companyInvoiceConfig.companyNotificationConfig') as FormGroup;
  }

  get subscriptionConfigGroup(): FormGroup {
    return this.configForm.get('companyConfig.companyInvoiceConfig.companySubscriptionConfig') as FormGroup;
  }

  getDynamicFieldsArray(scope: DynamicFieldsScope): FormArray {
    const path = scope === 'sale'
      ? 'companyConfig.companyInvoiceConfig.companySaleConfig.dynamicFields'
      : 'companyConfig.companyInvoiceConfig.companySubscriptionConfig.dynamicFields';
    return this.configForm.get(path) as FormArray;
  }

  private loadCompany(): void {
    this.initialLoad.set(true);
    this.companyService.getMyCompany().pipe(
      finalize(() => {
        this.initialLoad.set(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.company.set(response.body);
          this.patchConfig(response.body);
        } else {
          this.toastr.error(response.message ?? 'No se pudo cargar la configuración');
        }
      },
      error: (err) => {
        this.toastr.error(err.error?.message ?? 'Error al cargar la configuración');
      }
    });
  }

  private patchConfig(company: CompanyDTO): void {
    const invoice = company.companyConfig?.companyInvoiceConfig;
    const notification = invoice?.companyNotificationConfig;
    this.configForm.patchValue({
      companyConfig: {
        companyInvoiceConfig: {
          enableInvoice: invoice?.enableInvoice ?? false,
          enableAutoInvoice: invoice?.enableAutoInvoice ?? false,
          companyNotificationConfig: {
            domain: notification?.domain ?? '',
            sendEmail: notification?.sendEmail ?? '',
            endEmailAlias: notification?.endEmailAlias ?? '',
            replayEmail: notification?.replayEmail ?? '',
            replayEmailAlias: notification?.replayEmailAlias ?? ''
          },
          companySubscriptionConfig: {
            renewalsHourUTC: this.formatRenewalsHourForInput(
              invoice?.companySubscriptionConfig?.renewalsHourUTC
            )
          }
        }
      }
    });
    this.setDynamicFields('sale', invoice?.companySaleConfig?.dynamicFields ?? []);
    this.setDynamicFields('subscription', invoice?.companySubscriptionConfig?.dynamicFields ?? []);
  }

  private formatRenewalsHourForInput(value?: string | null): string {
    if (!value) return '00:00';
    const match = String(value).trim().match(/^(\d{2}:\d{2})/);
    return match?.[1] ?? '00:00';
  }

  private formatRenewalsHourForApi(value?: string | null): string {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '00:00:00';
    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
    if (/^\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
    return '00:00:00';
  }

  private createDynamicFieldGroup(field?: DynamicField): FormGroup {
    const existingKey = String(field?.fieldKey ?? '').trim();
    return this.fb.group({
      id: [field?.id ?? null],
      clientId: [field?.clientId ?? null],
      fieldKey: [
        { value: existingKey, disabled: !!existingKey },
        [Validators.required, Validators.maxLength(100)]
      ],
      label: [
        field?.label ?? '',
        [
          Validators.required,
          Validators.maxLength(200),
          CompanyConfigComponent.labelNoSpecialCharsValidator
        ]
      ],
      type: [field?.type ?? ('TEXT' as DynamicFieldType), Validators.required],
      required: [field?.required ?? false],
      includeInInvoice: [field?.includeInInvoice ?? false],
      optionsText: [(field?.options ?? []).join(', '), [Validators.maxLength(1000)]],
      validationRegex: [field?.validationRegex ?? '', [Validators.maxLength(500)]]
    });
  }

  /** Letras (con acentos), números y espacios; sin caracteres especiales. */
  private static readonly labelNoSpecialCharsValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '');
    if (!value) return null;
    return /^[\p{L}\p{N} ]+$/u.test(value) ? null : { specialChars: true };
  };

  private sanitizeFieldKey(label: string): string {
    return String(label ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  onLabelBlur(scope: DynamicFieldsScope, index: number): void {
    const group = this.getDynamicFieldsArray(scope).at(index) as FormGroup;
    const fieldKeyCtrl = group.get('fieldKey');
    if (!fieldKeyCtrl || fieldKeyCtrl.disabled) return;

    const currentKey = String(fieldKeyCtrl.value ?? '').trim();
    if (currentKey) return;

    const label = String(group.get('label')?.value ?? '');
    const generated = this.sanitizeFieldKey(label);
    if (generated) {
      fieldKeyCtrl.setValue(generated);
      fieldKeyCtrl.markAsTouched();
    }
    this.cdr.markForCheck();
  }

  private setDynamicFields(scope: DynamicFieldsScope, fields: DynamicField[]): void {
    const array = this.getDynamicFieldsArray(scope);
    array.clear();
    fields.forEach((field) => array.push(this.createDynamicFieldGroup(field)));
  }

  addDynamicField(scope: DynamicFieldsScope): void {
    const array = this.getDynamicFieldsArray(scope);
    array.push(this.createDynamicFieldGroup({
      type: 'TEXT',
      required: false,
      includeInInvoice: false
    }));
    this.cdr.markForCheck();
  }

  removeDynamicField(scope: DynamicFieldsScope, index: number): void {
    this.getDynamicFieldsArray(scope).removeAt(index);
    this.cdr.markForCheck();
  }

  isSelectFieldType(scope: DynamicFieldsScope, index: number): boolean {
    return this.getDynamicFieldsArray(scope).at(index)?.get('type')?.value === 'SELECT';
  }

  isFieldKeyLocked(scope: DynamicFieldsScope, index: number): boolean {
    return !!this.getDynamicFieldsArray(scope).at(index)?.get('fieldKey')?.disabled;
  }

  onDynamicFieldTypeChange(): void {
    this.cdr.markForCheck();
  }

  private mapDynamicFields(array: FormArray): DynamicField[] {
    return array.controls.map((control, index) => {
      const value = (control as FormGroup).getRawValue();
      const optionsText = String(value.optionsText ?? '').trim();
      const options = optionsText
        ? optionsText.split(',').map((o: string) => o.trim()).filter((o: string) => o.length > 0)
        : undefined;
      return {
        id: value.id ?? undefined,
        clientId: value.clientId ?? undefined,
        fieldKey: value.fieldKey?.trim() || undefined,
        label: value.label?.trim() || undefined,
        type: value.type,
        required: !!value.required,
        includeInInvoice: !!value.includeInInvoice,
        displayOrder: index,
        options: value.type === 'SELECT' ? (options ?? []) : undefined,
        validationRegex: value.validationRegex?.trim() || undefined
      };
    });
  }

  private buildCompanyConfigPayload(): CompanyConfig {
    const invoice = this.configForm.get('companyConfig.companyInvoiceConfig')?.getRawValue();
    const notification = invoice?.companyNotificationConfig;
    return {
      companyInvoiceConfig: {
        enableInvoice: !!invoice?.enableInvoice,
        enableAutoInvoice: !!invoice?.enableAutoInvoice,
        companySaleConfig: {
          dynamicFields: this.mapDynamicFields(this.saleDynamicFieldsArray)
        },
        companySubscriptionConfig: {
          renewalsHourUTC: this.formatRenewalsHourForApi(
            invoice?.companySubscriptionConfig?.renewalsHourUTC
          ),
          dynamicFields: this.mapDynamicFields(this.subscriptionDynamicFieldsArray)
        },
        companyNotificationConfig: {
          domain: notification?.domain?.trim() || undefined,
          sendEmail: notification?.sendEmail?.trim() || undefined,
          endEmailAlias: notification?.endEmailAlias?.trim() || undefined,
          replayEmail: notification?.replayEmail?.trim() || undefined,
          replayEmailAlias: notification?.replayEmailAlias?.trim() || undefined
        }
      }
    };
  }

  /** Las secciones de plataforma (Mandatos y notificaciones a clientes) también bloquean el guardado si son inválidas. */
  sectionsInvalid(): boolean {
    return this.mitConfigSection?.isInvalid() ?? false;
  }

  onSubmit(): void {
    if (this.configForm.invalid || this.sectionsInvalid()) {
      this.configForm.markAllAsTouched();
      this.cdr.markForCheck();
      if (this.sectionsInvalid()) {
        this.toastr.error('Revisa la configuración de Mandatos');
      }
      return;
    }

    const current = this.company();
    if (!current?.id) {
      this.toastr.error('No hay empresa cargada para guardar');
      return;
    }

    // Una sola petición y una sola transacción: las secciones de plataforma solo viajan si el usuario puede editarlas.
    const request: UpdateCompanyConfigRequest = { companyConfig: this.buildCompanyConfigPayload() };
    const mitConfig = this.mitConfigSection?.buildRequest();
    if (mitConfig) request.mitConfig = mitConfig;
    const customerNotificationConfig = this.customerNotificationSection?.buildRequest();
    if (customerNotificationConfig) request.customerNotificationConfig = customerNotificationConfig;

    this.loading.set(true);
    this.companyService.updateCompanyConfig(current.id, request).pipe(
      finalize(() => {
        this.loading.set(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.company.set(response.body);
          this.patchConfig(response.body);
          this.toastr.success('Configuración actualizada correctamente');
        } else {
          this.toastr.error(response.message ?? 'Error al actualizar la configuración');
        }
      },
      error: (err) => {
        this.toastr.error(err.error?.message ?? 'Error al actualizar la configuración');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/forecasts/sales-operations']);
  }
}
